import React, { useState, useEffect, useMemo } from 'react';
import chemicalService from '../../../services/chemicalService';

const UNIT_OPTIONS = ['Kg', 'Liter', 'Gram', 'ML', 'Piece', 'Meter'];
const PAYMENT_MODES = ['cash', 'bank_transfer', 'cheque', 'easypaisa', 'jazzcash'];

const blankItem = () => ({
  product_id: '',
  batch_id: '',
  quantity: '',
  unit: 'Kg',
  rate: '',
  discount_percent: 0,
  amount: '',
});

const SaleFormModal = ({ onClose, onSaved }) => {
  // ─── Master data ──
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(true);

  // ─── Per-product batch cache: { productId: [batch, batch, ...] } ──
  const [batchesByProduct, setBatchesByProduct] = useState({});
  const [loadingBatchesFor, setLoadingBatchesFor] = useState(new Set());

  // ─── Form state ──
  const [form, setForm] = useState({
    invoice_no: '',
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    gst_percent: 17,
    freight: 0,
    discount: 0,
    amount_paid: 0,
    payment_mode: 'cash',
    notes: '',
    items: [blankItem()],
  });

  const [submitting, setSubmitting] = useState(false);

  // Load customers + products
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMaster(true);
        const [customersRes, productsRes] = await Promise.all([
          chemicalService.getCustomers({ page: 1, limit: 1000 }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setCustomers((customersRes.data || []).filter((c) => c.is_active));
        setProducts((productsRes.data || []).filter((p) => p.is_active));
      } catch (err) {
        console.error('Error loading customers/products:', err);
        alert('❌ Failed to load customers or products');
      } finally {
        setLoadingMaster(false);
      }
    };
    load();
  }, []);

  // Selected customer (for credit warning)
  const selectedCustomer = useMemo(() => {
    return customers.find((c) => c.id === parseInt(form.customer_id, 10));
  }, [customers, form.customer_id]);

  // ─── Computed totals ──
  const { subtotal, gstAmount, grandTotal } = useMemo(() => {
    const sub = form.items.reduce((sum, item) => {
      const amt = parseFloat(item.amount);
      return sum + (isNaN(amt) ? 0 : amt);
    }, 0);
    const gstPct = parseFloat(form.gst_percent) || 0;
    const disc = parseFloat(form.discount) || 0;
    const frt = parseFloat(form.freight) || 0;
    const afterDiscount = sub - disc;
    const gst = afterDiscount * (gstPct / 100);
    const grand = afterDiscount + gst + frt;
    return { subtotal: sub, gstAmount: gst, grandTotal: grand };
  }, [form.items, form.gst_percent, form.discount, form.freight]);

  // Auto-clamp amount_paid to grand_total
  useEffect(() => {
    const paid = parseFloat(form.amount_paid) || 0;
    if (paid > grandTotal && grandTotal > 0) {
      setForm((prev) => ({ ...prev, amount_paid: grandTotal.toFixed(2) }));
    }
  }, [grandTotal, form.amount_paid]);

  // Payment status preview
  const paymentStatus = useMemo(() => {
    const paid = parseFloat(form.amount_paid) || 0;
    if (paid <= 0) return 'unpaid';
    if (paid >= grandTotal) return 'paid';
    return 'partial';
  }, [form.amount_paid, grandTotal]);

  // Credit limit check (if customer has one set)
  const creditWarning = useMemo(() => {
    if (!selectedCustomer) return null;
    const creditLimit = parseFloat(selectedCustomer.credit_limit) || 0;
    if (creditLimit <= 0) return null;

    const currentOutstanding = parseFloat(selectedCustomer.outstanding_balance) || 0;
    const newSaleUnpaid = grandTotal - (parseFloat(form.amount_paid) || 0);
    const projectedOutstanding = currentOutstanding + newSaleUnpaid;

    if (projectedOutstanding > creditLimit) {
      return {
        currentOutstanding,
        creditLimit,
        projectedOutstanding,
        excess: projectedOutstanding - creditLimit,
      };
    }
    return null;
  }, [selectedCustomer, grandTotal, form.amount_paid]);

  // ─── Fetch batches for a product (cached) ──
  const fetchBatchesForProduct = async (productId) => {
    if (!productId) return;
    if (batchesByProduct[productId]) return; // cached
    if (loadingBatchesFor.has(productId)) return; // in-flight

    setLoadingBatchesFor((prev) => new Set(prev).add(productId));
    try {
      const res = await chemicalService.getBatchesByProduct(productId);
      setBatchesByProduct((prev) => ({ ...prev, [productId]: res.data || [] }));
    } catch (err) {
      console.error('Error loading batches for product', productId, err);
      setBatchesByProduct((prev) => ({ ...prev, [productId]: [] }));
    } finally {
      setLoadingBatchesFor((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // ─── Handlers ──
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };

      // When product changes: reset batch, fill unit + default price, fetch batches
      if (field === 'product_id' && value) {
        const product = products.find((p) => p.id === parseInt(value, 10));
        items[index].batch_id = '';
        if (product?.base_unit) items[index].unit = product.base_unit;
        if (product?.default_price && !items[index].rate) {
          items[index].rate = product.default_price;
        }
        fetchBatchesForProduct(value);
      }

      // Auto-compute amount: qty × rate × (1 - discount_percent/100)
      if (field === 'quantity' || field === 'rate' || field === 'discount_percent') {
        const q = parseFloat(field === 'quantity' ? value : items[index].quantity);
        const r = parseFloat(field === 'rate' ? value : items[index].rate);
        const d = parseFloat(field === 'discount_percent' ? value : items[index].discount_percent) || 0;
        if (!isNaN(q) && !isNaN(r) && q > 0 && r >= 0) {
          const amt = q * r * (1 - d / 100);
          items[index].amount = amt.toFixed(2).replace(/\.?0+$/, '');
        }
      }

      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, blankItem()] }));
  };

  const removeItem = (index) => {
    if (form.items.length === 1) {
      alert('❌ A sale needs at least one item.');
      return;
    }
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.invoice_no.trim()) return alert('❌ Invoice number is required.');
    if (!form.customer_id) return alert('❌ Select a customer.');
    if (!form.invoice_date) return alert('❌ Invoice date is required.');

    // Validate items + stock
    for (let i = 0; i < form.items.length; i++) {
      const item = form.items[i];
      const idx = i + 1;
      if (!item.product_id) return alert(`❌ Item ${idx}: select a product.`);
      if (!item.batch_id) return alert(`❌ Item ${idx}: select a batch.`);
      if (!item.quantity || parseFloat(item.quantity) <= 0) return alert(`❌ Item ${idx}: quantity must be > 0.`);
      if (item.rate === '' || parseFloat(item.rate) < 0) return alert(`❌ Item ${idx}: rate must be ≥ 0.`);

      // Check stock client-side (backend will also check)
      const batches = batchesByProduct[item.product_id] || [];
      const batch = batches.find((b) => b.id === parseInt(item.batch_id, 10));
      if (batch && parseFloat(item.quantity) > parseFloat(batch.qty_available)) {
        return alert(
          `❌ Item ${idx}: Insufficient stock in batch ${batch.batch_no}.\n` +
          `Need ${item.quantity} ${item.unit}, only ${parseFloat(batch.qty_available)} available.`
        );
      }
    }

    if (grandTotal <= 0) return alert('❌ Grand total must be > 0.');
    if (parseFloat(form.amount_paid) > grandTotal) {
      return alert('❌ Amount paid cannot exceed grand total.');
    }

    // Credit warning confirmation (non-blocking)
    if (creditWarning) {
      const proceed = window.confirm(
        `⚠️ Credit Limit Warning\n\n` +
        `${selectedCustomer.name}'s outstanding will exceed credit limit.\n\n` +
        `Credit limit: Rs ${formatNumber(creditWarning.creditLimit)}\n` +
        `Current outstanding: Rs ${formatNumber(creditWarning.currentOutstanding)}\n` +
        `After this sale: Rs ${formatNumber(creditWarning.projectedOutstanding)}\n` +
        `Excess: Rs ${formatNumber(creditWarning.excess)}\n\n` +
        `Continue anyway?`
      );
      if (!proceed) return;
    }

    try {
      setSubmitting(true);
      const payload = {
        invoice_no: form.invoice_no.trim(),
        customer_id: parseInt(form.customer_id, 10),
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        subtotal: parseFloat(subtotal.toFixed(2)),
        gst_percent: parseFloat(form.gst_percent) || 0,
        gst_amount: parseFloat(gstAmount.toFixed(2)),
        freight: parseFloat(form.freight) || 0,
        discount: parseFloat(form.discount) || 0,
        grand_total: parseFloat(grandTotal.toFixed(2)),
        amount_paid: parseFloat(form.amount_paid) || 0,
        payment_mode: parseFloat(form.amount_paid) > 0 ? form.payment_mode : null,
        notes: form.notes.trim() || null,
        items: form.items.map((item) => ({
          product_id: parseInt(item.product_id, 10),
          batch_id: parseInt(item.batch_id, 10),
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          rate: parseFloat(item.rate),
          discount_percent: parseFloat(item.discount_percent) || 0,
          amount: parseFloat(item.amount),
        })),
      };

      await chemicalService.createSale(payload);
      alert('✅ Sale recorded! Stock deducted, customer balance updated.');
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create sale';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>💼 New Invoice</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Record a customer invoice — stock will be deducted from picked batches
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Invoice Header ──────────────── */}
            <div style={styles.sectionLabel}>Invoice Information</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Invoice No. <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.invoice_no}
                  onChange={(e) => handleChange('invoice_no', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. INV-0142"
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Customer <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.customer_id}
                  onChange={(e) => handleChange('customer_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingMaster}
                >
                  <option value="">
                    {loadingMaster
                      ? 'Loading customers...'
                      : customers.length === 0
                        ? '-- No active customers --'
                        : '-- Select customer --'}
                  </option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.ntn ? ` (NTN ${c.ntn})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Invoice Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.invoice_date}
                  onChange={(e) => handleChange('invoice_date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Due Date</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => handleChange('due_date', e.target.value)}
                  style={styles.input}
                  min={form.invoice_date || undefined}
                />
              </div>
            </div>

            {/* Customer info banner */}
            {selectedCustomer && (
              <div style={styles.customerBanner}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
                  <div>
                    <span style={styles.bannerLabel}>Current Outstanding:</span>
                    <strong style={{
                      color: parseFloat(selectedCustomer.outstanding_balance) > 0 ? '#e74c3c' : '#27ae60',
                      marginLeft: '8px',
                    }}>
                      Rs {formatNumber(selectedCustomer.outstanding_balance || 0)}
                    </strong>
                  </div>
                  {parseFloat(selectedCustomer.credit_limit) > 0 && (
                    <div>
                      <span style={styles.bannerLabel}>Credit Limit:</span>
                      <strong style={{ marginLeft: '8px' }}>
                        Rs {formatNumber(selectedCustomer.credit_limit)}
                      </strong>
                    </div>
                  )}
                </div>
                {creditWarning && (
                  <div style={styles.creditWarning}>
                    ⚠ This sale will push outstanding to Rs {formatNumber(creditWarning.projectedOutstanding)},
                    exceeding the credit limit by Rs {formatNumber(creditWarning.excess)}
                  </div>
                )}
              </div>
            )}

            {/* ─── Items ─────────────────────────── */}
            <div style={styles.sectionLabel}>
              Items ({form.items.length})
              <span style={styles.deductHint}>(stock auto-deducted on save)</span>
            </div>

            <div style={styles.itemsList}>
              {form.items.map((item, index) => {
                const batches = batchesByProduct[item.product_id] || [];
                const isLoadingBatches = loadingBatchesFor.has(item.product_id);
                const pickedBatch = batches.find((b) => b.id === parseInt(item.batch_id, 10));
                const available = pickedBatch ? parseFloat(pickedBatch.qty_available) : 0;
                const needed = parseFloat(item.quantity) || 0;
                const shortage = needed > available && item.batch_id;

                return (
                  <div
                    key={index}
                    style={{
                      ...styles.itemCard,
                      ...(shortage ? styles.itemCardWarning : {}),
                    }}
                  >
                    <div style={styles.itemHeader}>
                      <div style={styles.itemIndex}>{index + 1}</div>
                      <span style={styles.itemTitle}>Item {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        style={styles.removeButton}
                        disabled={form.items.length === 1}
                      >
                        🗑️
                      </button>
                    </div>

                    {/* Row 1: product + batch picker */}
                    <div style={styles.itemRow}>
                      <div style={{ flex: 2, minWidth: '200px' }}>
                        <label style={styles.miniLabel}>Product *</label>
                        <select
                          value={item.product_id}
                          onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                          style={styles.input}
                          required
                          disabled={loadingMaster}
                        >
                          <option value="">-- Select product --</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code} — {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 3, minWidth: '260px' }}>
                        <label style={styles.miniLabel}>Batch *</label>
                        {!item.product_id ? (
                          <input style={{ ...styles.input, ...styles.inputDisabled }} disabled placeholder="Select product first" />
                        ) : isLoadingBatches ? (
                          <input style={{ ...styles.input, ...styles.inputDisabled }} disabled placeholder="Loading batches..." />
                        ) : batches.length === 0 ? (
                          <select style={{ ...styles.input, borderColor: '#e74c3c' }} disabled>
                            <option>No stock available for this product</option>
                          </select>
                        ) : (
                          <select
                            value={item.batch_id}
                            onChange={(e) => handleItemChange(index, 'batch_id', e.target.value)}
                            style={styles.input}
                            required
                          >
                            <option value="">-- Pick batch --</option>
                            {batches.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.batch_no} — {parseFloat(b.qty_available).toFixed(2).replace(/\.?0+$/, '')} {b.product_unit} available
                                {b.expiry_date ? ` (exp ${formatShortDate(b.expiry_date)})` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* Row 2: qty + unit + rate + discount% + amount */}
                    <div style={styles.itemRow}>
                      <div style={{ flex: 1, minWidth: '100px' }}>
                        <label style={styles.miniLabel}>
                          Qty * {pickedBatch && (
                            <span style={{
                              color: shortage ? '#e74c3c' : '#95a5a6',
                              fontWeight: '600',
                              marginLeft: '4px',
                            }}>
                              (avail {available})
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          style={{
                            ...styles.input,
                            ...(shortage ? { borderColor: '#e74c3c', background: '#fdecea' } : {}),
                          }}
                          placeholder="0"
                          required
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '90px' }}>
                        <label style={styles.miniLabel}>Unit *</label>
                        <select
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          style={styles.input}
                          required
                        >
                          {UNIT_OPTIONS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: '110px' }}>
                        <label style={styles.miniLabel}>Rate (Rs) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          style={styles.input}
                          placeholder="0"
                          required
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '90px' }}>
                        <label style={styles.miniLabel}>Disc %</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={item.discount_percent}
                          onChange={(e) => handleItemChange(index, 'discount_percent', e.target.value)}
                          style={styles.input}
                          placeholder="0"
                        />
                      </div>
                      <div style={{ flex: 1.3, minWidth: '120px' }}>
                        <label style={styles.miniLabel}>Amount (Rs) *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.amount}
                          onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                          style={{ ...styles.input, fontWeight: '700', color: '#27ae60' }}
                          placeholder="0"
                          required
                        />
                      </div>
                    </div>

                    {shortage && (
                      <div style={styles.shortageLine}>
                        ⚠ Need {needed} {item.unit}, only {available} available in batch {pickedBatch.batch_no}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button type="button" onClick={addItem} style={styles.addItemButton}>
              + Add item
            </button>

            {/* ─── Totals ──────────────────────── */}
            <div style={styles.sectionLabel}>Totals</div>
            <div style={styles.totalsCard}>
              <div style={styles.totalsRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>GST %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.gst_percent}
                    onChange={(e) => handleChange('gst_percent', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Discount (Rs)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.discount}
                    onChange={(e) => handleChange('discount', e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Freight (Rs)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.freight}
                    onChange={(e) => handleChange('freight', e.target.value)}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.totalsBreakdown}>
                <div style={styles.totalLine}>
                  <span>Subtotal</span>
                  <strong>Rs {formatNumber(subtotal)}</strong>
                </div>
                {parseFloat(form.discount) > 0 && (
                  <div style={styles.totalLine}>
                    <span>Discount</span>
                    <strong style={{ color: '#e74c3c' }}>− Rs {formatNumber(form.discount)}</strong>
                  </div>
                )}
                <div style={styles.totalLine}>
                  <span>GST ({form.gst_percent}%)</span>
                  <strong>Rs {formatNumber(gstAmount)}</strong>
                </div>
                {parseFloat(form.freight) > 0 && (
                  <div style={styles.totalLine}>
                    <span>Freight</span>
                    <strong>Rs {formatNumber(form.freight)}</strong>
                  </div>
                )}
                <div style={{ ...styles.totalLine, ...styles.grandTotal }}>
                  <span>Grand Total</span>
                  <strong>Rs {formatNumber(grandTotal)}</strong>
                </div>
              </div>
            </div>

            {/* ─── Payment ─────────────────────── */}
            <div style={styles.sectionLabel}>Payment</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount Received (Rs)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={grandTotal}
                  value={form.amount_paid}
                  onChange={(e) => handleChange('amount_paid', e.target.value)}
                  style={styles.input}
                  placeholder="0"
                />
                <span style={styles.hint}>Leave at 0 if unpaid. Max Rs {formatNumber(grandTotal)}</span>
              </div>
              {parseFloat(form.amount_paid) > 0 && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Mode</label>
                  <select
                    value={form.payment_mode}
                    onChange={(e) => handleChange('payment_mode', e.target.value)}
                    style={styles.input}
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>{formatPaymentMode(m)}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <div style={{ marginTop: '6px' }}>
                  <span style={getStatusBadge(paymentStatus)}>
                    {paymentStatus === 'paid' ? '✓ Paid' : paymentStatus === 'partial' ? 'Partial' : 'Unpaid'}
                  </span>
                  {paymentStatus === 'partial' && (
                    <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                      Rs {formatNumber(grandTotal - parseFloat(form.amount_paid || 0))} remaining
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ ...styles.formGroup, marginTop: '8px' }}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Optional notes about this invoice..."
                rows="2"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : '💾 Save Invoice'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatShortDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    cheque: 'Cheque',
    easypaisa: 'EasyPaisa',
    jazzcash: 'JazzCash',
  };
  return map[m] || m;
};

const getStatusBadge = (status) => {
  const palette = {
    paid:    { bg: '#d4edda', fg: '#155724' },
    partial: { bg: '#fff3cd', fg: '#856404' },
    unpaid:  { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[status] || palette.unpaid;
  return {
    background: c.bg, color: c.fg,
    padding: '6px 14px', borderRadius: '12px',
    fontSize: '13px', fontWeight: '700',
    display: 'inline-block',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '1100px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  sectionLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px',
    marginTop: '20px', marginBottom: '12px',
    paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  deductHint: {
    background: '#fdecea', color: '#721c24',
    padding: '2px 10px', borderRadius: '10px',
    fontSize: '11px', fontWeight: '700',
    textTransform: 'none', letterSpacing: 0,
  },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '12px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: {
    fontSize: '11px', fontWeight: '600',
    color: '#7f8c8d', marginBottom: '4px', display: 'block',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  inputDisabled: {
    background: '#f5f7fa', color: '#7f8c8d', cursor: 'not-allowed',
  },
  hint: { fontSize: '11px', color: '#95a5a6', fontStyle: 'italic' },

  customerBanner: {
    background: '#e3f2fd',
    border: '1px solid #bbdefb',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '12px',
    fontSize: '13px',
  },
  bannerLabel: { color: '#7f8c8d', fontWeight: '600' },
  creditWarning: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #bbdefb',
    color: '#721c24',
    fontWeight: '600',
    fontSize: '13px',
  },

  itemsList: {
    display: 'flex', flexDirection: 'column', gap: '12px',
    marginBottom: '12px',
  },
  itemCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '14px',
    border: '1px solid #e0e0e0',
  },
  itemCardWarning: {
    background: '#fff8e1',
    border: '1px solid #f9e0a0',
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px dashed #e0e0e0',
  },
  itemIndex: {
    width: '24px', height: '24px',
    borderRadius: '50%',
    background: '#e74c3c',
    color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700',
    flexShrink: 0,
  },
  itemTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    flex: 1,
  },
  itemRow: {
    display: 'flex', gap: '10px', flexWrap: 'wrap',
    marginBottom: '8px',
  },
  shortageLine: {
    marginTop: '8px',
    padding: '8px 12px',
    background: '#fdecea',
    color: '#721c24',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
  },
  removeButton: {
    padding: '6px 12px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  addItemButton: {
    width: '100%',
    padding: '12px',
    background: 'white',
    color: '#e74c3c',
    border: '2px dashed #e74c3c',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '12px',
  },

  totalsCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '16px',
    border: '1px solid #e0e0e0',
  },
  totalsRow: {
    display: 'flex', gap: '16px', flexWrap: 'wrap',
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: '1px dashed #e0e0e0',
  },
  totalsBreakdown: { display: 'flex', flexDirection: 'column', gap: '6px' },
  totalLine: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: '14px',
    color: '#2c3e50',
  },
  grandTotal: {
    marginTop: '6px',
    paddingTop: '12px',
    borderTop: '2px solid #2c3e50',
    fontSize: '18px',
    fontWeight: '700',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default SaleFormModal;